/**
 * RecordTopPanel - the platform's standard top panel for a record page
 * (claim today; registration, transfer and inspection to follow).
 *
 * One layout for every record: back link, the record's number and status,
 * a one-line subtitle, quiet controls, an "Actions" menu and exactly ONE
 * primary action, then a row of facts (the next step first) and the journey.
 *
 * Colours come from the `--panel` / `--panel-ink` tokens: warm cream by
 * default, and a tenant can set its own panel colours in Appearance (ink, or a
 * pale wash of its brand). The primary button takes the tenant accent via the
 * shared Button. Nothing here knows which record or tenant it is showing - the
 * page passes data and callbacks in.
 */

import type { ReactNode } from "react";
import { ArrowLeft, Check, ChevronDown, Info, Minus, AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { SimpleTooltip } from "@/shared/components/ui/tooltip";
import { cn } from "@/shared/lib/utils";

export interface RecordTopPanelFact {
  key: string;
  label: string;
  value: ReactNode;
  /** A second, smaller line under the value (e.g. what a step still needs). */
  detail?: ReactNode;
  /** More about this fact, on hover/focus of a small info mark - kept off
   *  the page so the row stays calm. */
  info?: ReactNode;
  emphasis?: boolean;
  testId?: string;
}

export interface RecordTopPanelAction {
  key: string;
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
  /** Irreversible or record-ending actions: listed last, in red. */
  destructive?: boolean;
  /** "record" = controls about the record itself (flag, labels, access),
   *  listed after the workflow actions. */
  section?: "workflow" | "record";
}

export interface RecordJourneyNode {
  key: string;
  label: string;
  status: "completed" | "current" | "blocked" | "skipped" | "future";
  /** Who acts at this stage, in words (config-authored). */
  meta?: string | null;
  metaColor?: string | null;
  /** Shown on hover: why a stage is blocked or skipped. */
  tooltip?: ReactNode;
  tone?: "default" | "error" | "ok";
}

interface RecordTopPanelProps {
  backLabel: string;
  onBack: () => void;
  title: ReactNode;
  titleClassName?: string;
  status?: ReactNode;
  chips?: ReactNode;
  subtitle?: ReactNode;
  quietControls?: ReactNode;
  menuActions?: RecordTopPanelAction[];
  primaryAction?: ReactNode;
  /** Replaces the facts' leading next step - one status message at a time. */
  notice?: ReactNode;
  facts?: RecordTopPanelFact[];
  journey?: ReactNode;
}

export function RecordTopPanel({
  backLabel,
  onBack,
  title,
  titleClassName,
  status,
  chips,
  subtitle,
  quietControls,
  menuActions = [],
  primaryAction,
  notice,
  facts = [],
  journey,
}: RecordTopPanelProps) {
  const { t } = useTranslation();
  const regular = menuActions.filter(
    (a) => !a.destructive && a.section !== "record",
  );
  const record = menuActions.filter(
    (a) => !a.destructive && a.section === "record",
  );
  const destructive = menuActions.filter((a) => a.destructive);
  const menuGroups = [regular, record, destructive].filter((g) => g.length > 0);

  return (
    <section
      data-testid="record-top-panel"
      className="mb-4 rounded-xl border border-[var(--panel-rule)] bg-[var(--panel)] px-5 pt-3 pb-4 font-normal text-[var(--panel-ink)] md:px-6"
    >
      <Button
        onClick={onBack}
        variant="link"
        className="mb-1.5 flex h-auto cursor-pointer items-center gap-1 p-0 text-[13px] font-normal text-[var(--panel-ink-2)] hover:text-[var(--panel-ink)]"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        {backLabel}
      </Button>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
            <h1
              className={cn(
                "m-0 text-[24px] leading-[1.2] font-semibold tracking-[-0.01em] text-[var(--panel-ink)] tabular-nums",
                titleClassName,
              )}
            >
              {title}
            </h1>
            <span data-testid="record-top-panel-status" className="contents">
              {status}
            </span>
            {chips}
          </div>
          {subtitle && (
            <p className="m-0 text-[13px] leading-[1.45] text-[var(--panel-ink-2)]">
              {subtitle}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1.5 lg:shrink-0 lg:flex-nowrap lg:justify-end">
          {quietControls}
          {menuActions.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    variant="outline"
                    className="h-8 cursor-pointer gap-1.5 rounded-[8px] border-[var(--panel-rule)] bg-[var(--bg)] px-3 text-[13px] text-[var(--ink)]"
                  />
                }
              >
                {t("records.topPanel.actions", "Actions")}
                <ChevronDown className="size-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {menuGroups.map((group, idx) => (
                  <DropdownMenuGroup key={group[0].key}>
                    {idx > 0 && <DropdownMenuSeparator />}
                    {group.map((action) => (
                      <DropdownMenuItem
                        key={action.key}
                        variant={action.destructive ? "destructive" : "default"}
                        disabled={action.disabled}
                        onClick={action.onSelect}
                      >
                        {action.icon}
                        {action.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuGroup>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {primaryAction}
        </div>
      </div>

      {(notice || facts.length > 0) && (
        <div className="mt-3 flex flex-wrap items-start gap-x-8 gap-y-2 border-t border-[var(--panel-rule)] pt-2.5">
          {notice}
          {facts.map((fact) => (
            <div key={fact.key} className="min-w-0">
              <p className="m-0 text-[13px] text-[var(--panel-ink-2)]">
                {fact.label}
              </p>
              <p className="m-0 flex items-center gap-1 text-[13px] leading-[1.35] text-[var(--panel-ink)]">
                <span
                  className={fact.emphasis ? "font-medium" : "font-normal"}
                  data-testid={fact.testId}
                >
                  {fact.value}
                </span>
                {fact.info && (
                  <SimpleTooltip content={fact.info}>
                    <span
                      tabIndex={0}
                      aria-label={typeof fact.info === "string" ? fact.info : undefined}
                      className="inline-flex cursor-help rounded-full text-[var(--panel-ink-2)] outline-none hover:text-[var(--panel-ink)] focus-visible:ring-2 focus-visible:ring-[var(--green-border)]"
                    >
                      <Info className="h-3.5 w-3.5" />
                    </span>
                  </SimpleTooltip>
                )}
              </p>
              {fact.detail && (
                <p className="m-0 max-w-[420px] text-[13px] leading-[1.4] text-[var(--panel-ink-2)]">
                  {fact.detail}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {journey && <div className="mt-3">{journey}</div>}
    </section>
  );
}

/**
 * RecordJourney - the swim line. Every stage gets an equal column so long
 * names wrap instead of clipping; completed stages are green (done means done
 * on every tenant), the current stage takes the tenant accent.
 */
export function RecordJourney({ nodes }: { nodes: RecordJourneyNode[] }) {
  return (
    <div className="relative">
      <div
        aria-hidden="true"
        className="absolute top-[9px] right-[4%] left-[4%] h-px bg-[var(--panel-rule)]"
      />
      <ol className="relative m-0 flex list-none gap-1 p-0">
        {nodes.map((node) => {
          // Who acts is shown under the stage the claim is at; for the other
          // stages it is one hover away, so the line stays calm.
          const showMeta =
            node.status === "current" || node.status === "blocked";
          const tooltip =
            node.tooltip ??
            (!showMeta && node.meta ? node.meta : null);
          const dot = (
            <span
              className={cn(
                "relative z-[1] flex h-5 w-5 items-center justify-center rounded-full border-[1.5px]",
                node.status === "completed" &&
                  "border-[var(--status-ok)] bg-[var(--status-ok)] text-white",
                node.status === "current" &&
                  (node.tone === "error"
                    ? "border-[var(--status-error)] bg-[var(--panel)]"
                    : node.tone === "ok"
                      ? "border-[var(--status-ok)] bg-[var(--panel)]"
                      : "border-[var(--green)] bg-[var(--panel)] shadow-[0_0_0_3px_var(--green-bg)]"),
                node.status === "blocked" &&
                  "border-[var(--status-warn)] bg-[var(--status-warn)] text-white",
                node.status === "skipped" &&
                  "border-dashed border-[var(--panel-rule)] bg-[var(--panel)] text-[var(--panel-ink-2)]",
                node.status === "future" &&
                  "border-[var(--panel-rule)] bg-[var(--panel)]",
              )}
            >
              {node.status === "completed" && <Check className="h-3 w-3" />}
              {node.status === "blocked" && (
                <AlertTriangle className="h-3 w-3" />
              )}
              {node.status === "skipped" && <Minus className="h-2.5 w-2.5" />}
              {node.status === "current" && (
                <span
                  className={cn(
                    "h-2 w-2 rounded-full",
                    node.tone === "error"
                      ? "bg-[var(--status-error)]"
                      : node.tone === "ok"
                        ? "bg-[var(--status-ok)]"
                        : "bg-[var(--green)]",
                  )}
                />
              )}
            </span>
          );
          return (
            <li
              key={node.key}
              data-state-key={node.key}
              data-node-status={node.status}
              aria-current={node.status === "current" ? "step" : undefined}
              className="flex min-w-0 flex-1 flex-col items-center gap-1 text-center"
            >
              {tooltip ? (
                <SimpleTooltip content={tooltip}>
                  <span className="inline-flex cursor-help">{dot}</span>
                </SimpleTooltip>
              ) : (
                dot
              )}
              <span
                className={cn(
                  "text-[13px] leading-[1.3]",
                  node.status === "current"
                    ? "font-medium text-[var(--panel-ink)]"
                    : "text-[var(--panel-ink-2)]",
                  node.status === "skipped" && "line-through",
                  node.status === "blocked" && "text-[var(--status-warn)]",
                )}
              >
                {node.label}
              </span>
              {showMeta && node.meta && (
                <span
                  className="text-[13px] leading-[1.3] text-[var(--panel-ink-2)]"
                  // role_label_color is tenant-authored config resolved at
                  // runtime - the documented inline-style exception.
                  style={node.metaColor ? { color: node.metaColor } : undefined}
                >
                  {node.meta}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
